import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import { DeleteOneanalyticsDashboardDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { DashboardNode } from 'modules/ai_reports/lib/types'

type Props = {
  itemToDelete: DashboardNode | null
  setItemToDelete: (item: DashboardNode | null) => void
}

const DeleteDashboardDialog = ({ itemToDelete, setItemToDelete }: Props) => {
  const [deleteDashboard] = useMutation(DeleteOneanalyticsDashboardDocument, {
    extractorFn: (data) => data.oneAnalyticsMutations.deleteOneanalyticsDashboard,
  })

  const handleClose = () => setItemToDelete(null)

  const onDelete = async (item: DashboardNode) => {
    const result = await deleteDashboard({
      variables: { id: item.id },
      update(cache) {
        cache.evict({
          id: cache.identify({ id: item.id, __typename: 'OneAnalyticsDashboard' }),
        })
        cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'oneAnalytics',
        })
        cache.gc()
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ai_reports.dashboards.notifications.delete_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ai_reports.dashboards.notifications.delete_success'),
      })
    }

    handleClose()
  }

  return (
    <Dialog
      onClose={handleClose}
      open={itemToDelete !== null}
      actions={{
        primary: {
          icon: Delete,
          label: i18n.t('common.delete'),
          onClick: () => {
            if (!itemToDelete) throw new Error('Missing dashboard to delete')
            void onDelete(itemToDelete)
          },
          variant: 'critical',
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: handleClose,
        },
      }}
      header={{
        description: i18n.t('ai_reports.dashboards.delete_description', {
          dashboardName: itemToDelete?.title,
        }),
        title: i18n.t('ai_reports.dashboards.delete_title'),
        type: 'critical',
      }}
    />
  )
}

export default DeleteDashboardDialog
